import {
  HttpClient,
  HttpContext,
  HttpDownloadProgressEvent,
  HttpEvent,
  HttpEventType
} from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { sift } from "radash";
import { from, map, mergeMap, Observable } from "rxjs";
import { distinct, filter, take, toArray } from "rxjs/operators";
import { CustomerLight } from "src/app/backend/models/customer/customer";
import {
  Segmentation,
  SegmentationCreate,
  SegmentationCustomer,
  SegmentationUpdate
} from "src/app/backend/models/segmentation";
import { HTTP_CONTEXT_TOKEN_NO_SWAP_DEEP_PROPERTY_ID } from "src/app/core/http-context-tokens";
import { tryParseJson } from "src/app/support/json";
import { UserNotify } from "src/app/support/notify-decorator";
import {
  omitUndefinedAndNullProperties,
  swapDeepProperty
} from "src/app/support/object";
import { PaginatedResult } from "src/app/support/paginated-result";
import { environment } from "src/environments/environment";

@Injectable()
export class SegmentationBackend {
  private readonly http = inject(HttpClient);

  list(): Observable<PaginatedResult<Segmentation>> {
    return this.http.get<PaginatedResult<Segmentation>>(
      `${environment.snoots.api.directory}/segmentations`
    );
  }

  loadById(id: string): Observable<Segmentation> {
    return this.http.get<Segmentation>(
      `${environment.snoots.api.directory}/segmentations/${id}`
    );
  }

  @UserNotify({
    successMessage: marker("Lista creata"),
    errorMessage: marker("Impossibile creare la lista")
  })
  create(payload: SegmentationCreate): Observable<Segmentation> {
    return this.http.post<Segmentation>(
      `${environment.snoots.api.directory}/segmentations`,
      payload,
      {
        context: new HttpContext().set(
          HTTP_CONTEXT_TOKEN_NO_SWAP_DEEP_PROPERTY_ID,
          true
        )
      }
    );
  }

  @UserNotify({
    successMessage: marker("Lista modificata"),
    errorMessage: marker("Impossibile modificare la lista")
  })
  update(id: string, payload: SegmentationUpdate): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.directory}/segmentations/${id}`,
      payload,
      {
        context: new HttpContext().set(
          HTTP_CONTEXT_TOKEN_NO_SWAP_DEEP_PROPERTY_ID,
          true
        )
      }
    );
  }

  @UserNotify({
    successMessage: marker("Lista eliminata"),
    errorMessage: marker("Impossibile eliminare la lista")
  })
  delete(id: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.snoots.api.directory}/segmentations/${id}`
    );
  }

  stream({
    id,
    entityId,
    limit
  }: {
    id: string;
    entityId?: string | null;
    limit?: number | undefined;
  }): Observable<SegmentationCustomer> {
    return this.http
      .get(`${environment.snoots.api.directory}/segmentations/${id}/entities`, {
        params: omitUndefinedAndNullProperties({
          entityId,
          limit: entityId ? undefined : limit
        }),
        observe: "events",
        reportProgress: true,
        responseType: "text"
      })
      .pipe(
        filter((event: HttpEvent<any>) => {
          if (event.type !== HttpEventType.DownloadProgress) {
            return false;
          }

          const progressEvent = event as HttpDownloadProgressEvent;
          const lines = progressEvent.partialText?.split("\n") || [];
          return lines.length !== 0;
        }),
        map((event: HttpEvent<any>) => {
          const progressEvent = event as HttpDownloadProgressEvent;
          return progressEvent.partialText!.split("\n");
        }),
        mergeMap((lines: string[]) => {
          return from(
            sift(
              sift(lines).map((line) =>
                tryParseJson<SegmentationCustomer | null>(line, null)
              )
            )
          );
        }),
        map((result: SegmentationCustomer) => {
          return {
            ...result,
            doc: swapDeepProperty("_id", "id", result.doc)
          };
        }),
        distinct((result) => {
          // Se l'id è undefined, restituiamo un simbolo unico per ogni chiamata
          // Questo "inganna" distinct facendogli credere che sia sempre un valore nuovo
          return result.doc?.id === undefined ? Symbol() : result.doc.id;
        }),
        limit ? take(limit) : map((result) => result)
      );
  }

  loadCustomers({
    id,
    customerId,
    limit
  }: {
    id: string;
    customerId?: string | null;
    limit?: number | undefined;
  }): Observable<CustomerLight[]> {
    return this.stream({ id, entityId: customerId, limit }).pipe(
      toArray(),
      map((results: SegmentationCustomer[]) => {
        return results.map((result) => {
          return result.doc;
        });
      })
    );
  }

  checkCustomer({
    segmentationId,
    customerId
  }: {
    segmentationId: string;
    customerId: string;
  }): Observable<boolean> {
    return this.loadCustomers({ id: segmentationId, customerId }).pipe(
      map((customers: CustomerLight[]) => {
        return customers.length > 0;
      })
    );
  }

  @UserNotify({
    successMessage: marker("Contatti esportati in Excel"),
    errorMessage: marker("Impossibile esportare i contatti"),
    progressIndeterminate: true,
    progressMessage: marker("Esportazione contatti in corso")
  })
  exportMassiveExcel(id: string): Observable<void> {
    return this.http.post<void>(
      `${environment.snoots.api.directory}/segmentations/${id}/exports`,
      {}
    );
  }
}
